// src/components/layout/CustomerHeader.jsx
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import CustomerUserDropdownMenu from '@/components/common/CustomerUserDropdownMenu';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { getFileUrl } from '@/services/CommonService';
import { setAdminSideDrawer } from '@/store/actions';

const CustomerHeader = ({ title, faviconIcon }) => {
  const dispatch = useDispatch();

  const { generalSetting, adminSideDrawer } = useSelector((state) => state?.GeneralSetting);
  const customer = useSelector((s) => s.Auth.userData);

  const appName = useMemo(() => {
    const fromSettings =
      generalSetting?.site_name ||
      generalSetting?.company_name ||
      generalSetting?.app_name ||
      null;
    const fromLS = (() => {
      try { return localStorage.getItem('company_name') || null; } catch { return null; }
    })();
    return fromSettings || fromLS || 'TabanFood';
  }, [generalSetting?.site_name, generalSetting?.company_name, generalSetting?.app_name]);

  const nowParam = () => `r=${Date.now()}`;

  // Logo src: prefer *_url (cache-busted), fall back to file name or default
  const themeLogoSrc = useMemo(() => {
    if (generalSetting?.theme_logo_url) {
      const sep = generalSetting.theme_logo_url.includes('?') ? '&' : '?';
      return `${generalSetting.theme_logo_url}${sep}${nowParam()}`;
    }
    const fn = generalSetting?.theme_logo;
    if (fn) return `${getFileUrl(fn, 'theme')}?${nowParam()}`;
    return '/assets/images/theme/theme-logo.png';
  }, [generalSetting?.theme_logo_url, generalSetting?.theme_logo]);

  // Favicon src: same precedence as Admin
  const faviconSrc = useMemo(() => {
    if (faviconIcon) return faviconIcon;
    const url = generalSetting?.fav_icon_url || generalSetting?.theme_logo_url || null;
    if (url) {
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}${nowParam()}`;
    }
    const fav = generalSetting?.fav_icon;
    if (fav) return `${getFileUrl(fav, 'theme')}?${nowParam()}`;
    const logo = generalSetting?.theme_logo;
    if (logo) return `${getFileUrl(logo, 'theme')}?${nowParam()}`;
    return '/favicon.ico';
  }, [faviconIcon, generalSetting?.fav_icon_url, generalSetting?.theme_logo_url, generalSetting?.fav_icon, generalSetting?.theme_logo]);

  const handleDrawer = () => {
    const next = !adminSideDrawer;
    dispatch(setAdminSideDrawer(next));
    document.body.style.overflowY = next ? 'hidden' : 'auto';
  };

  // Force remount when avatar changes (same trick you used)
  const dropdownKey = `${customer?.file_name || ''}-${customer?.updatedAt || ''}`;

  return (
    <>
      <Helmet>
        <title>{title && title !== '' ? `${title} • ${appName}` : `${appName} (Customer)`}</title>
        <link rel="icon" href={faviconSrc} />
      </Helmet>

      <header className="db-header">
        {/* Match AdminHeader sizing: w-56 container + max-h-12 logo */}
        <Link
          className="flex-shrink-0 px-3 py-2 w-56 flex items-center justify-center"
          to="/customer/dashboard"
          title="Customer Dashboard"
        >
          <img
            key={themeLogoSrc}
            className="max-h-12 w-auto object-contain"
            src={themeLogoSrc}
            alt="logo"
            onError={(e) => { e.currentTarget.src = '/assets/images/theme/theme-logo.png'; }}
          />
        </Link>

        <div className="flex items-center justify-end w-full gap-4">
          <button
            type="button"
            onClick={handleDrawer}
            className="fa-solid fa-align-left db-header-nav w-9 h-9 rounded-lg text-primary bg-primary/5"
            aria-label="Toggle menu"
            title="Toggle sidebar"
          />
          <CustomerUserDropdownMenu key={dropdownKey} />
        </div>
      </header>
    </>
  );
};

export default CustomerHeader;
